import * as Yup from "yup"
import { BenefitsEnrollmentStateEnum, BenefitsPlan, Maybe } from "@oysterhr/schema"
import { ContributionShape } from "../types"
import { useTranslation } from "react-i18next"

const useSchemaShapes = () => {
  const { t } = useTranslation("benefits/EnrollmentForm")

  const testNumberPositive = Yup.mixed()
    .testNumber(t("validation.number", { ns: "Forms" }))
    .testPositive(t("validation.positive", { ns: "Forms" }))

  function noPriceAndEnrolled(value: unknown, enrollmentState?: BenefitsEnrollmentStateEnum) {
    return value === "" && enrollmentState === BenefitsEnrollmentStateEnum.Enrolled
  }

  function testPricePaidProviderCurrency(enrollmentState?: BenefitsEnrollmentStateEnum) {
    return Yup.lazy(value => {
      let base = Yup.mixed()
      if (noPriceAndEnrolled(value, enrollmentState)) {
        base = base.required(t("validation.notBlank", { ns: "Forms" }))
      }

      return base
    })
  }

  function testPricePaidProviderAmount(enrollmentState?: BenefitsEnrollmentStateEnum) {
    return Yup.lazy(value => {
      let base = testNumberPositive
      if (noPriceAndEnrolled(value, enrollmentState)) {
        base = base.required(t("validation.notBlank"))
      }
      return base
    })
  }

  const externalIdObjectShape = ({
    externalId,
    state,
  }: {
    externalId?: Maybe<string>
    state?: BenefitsEnrollmentStateEnum
  }) => {
    return Yup.lazy(() => {
      if (externalId && state === BenefitsEnrollmentStateEnum.Enrolled) {
        return Yup.string().required(t("errors.externalId.cannotRemove"))
      }
      return Yup.string()
    })
  }

  function pricePaidProviderObjectShape({
    enrollmentState,
  }: {
    enrollmentState?: BenefitsEnrollmentStateEnum
  }) {
    const testCurrency = testPricePaidProviderCurrency(enrollmentState)
    const testAmount = testPricePaidProviderAmount(enrollmentState)
    return Yup.object().testMoneyInput({
      testCurrency,
      testAmount,
      message: t("errors.bothCurrencyAndAmount"),
    })
  }

  function planObjectShape() {
    return Yup.object().shape({
      id: Yup.string().required(t("validation.required", { ns: "Forms" })),
    }) as Yup.ObjectSchema<BenefitsPlan>
  }

  /**
   * Validate to enter percent, money.amount or both
   * @param testName test identifier name
   * @param planContributionSetting contains config props min, max
   * @returns Yup.object
   */
  function customerContributionObjectShape({
    testName,
    planContributionSetting,
  }: ContributionShape) {
    const { minimumPercentage, maximumPercentage, minimumAmountSubunit, maximumAmountSubunit } =
      planContributionSetting || {}

    return Yup.object()
      .shape({
        percent: testNumberPositive.testPercentageBounds({
          min: minimumPercentage || undefined,
          max: maximumPercentage || undefined,
        }),
        amount: testNumberPositive.testNumericBounds({
          min: minimumAmountSubunit || undefined,
          max: maximumAmountSubunit || undefined,
        }),
      })
      .test(testName, t("errors.percentOrAmountOrBoth"), function (testPayload) {
        return !!(testPayload?.percent || testPayload?.amount)
      })
  }

  function tmContributionObjectShape({ testName }: { testName: string }) {
    return Yup.object()
      .shape({
        percent: testNumberPositive.testPercentageBounds({}),
        amount: testNumberPositive.testNumericBounds({}),
      })
      .test(testName, t("errors.percentOrAmountOrBoth"), function (testPayload) {
        return !!(testPayload?.percent || testPayload?.amount)
      })
  }

  return {
    pricePaidProviderObjectShape,
    planObjectShape,
    externalIdObjectShape,
    customerContributionObjectShape,
    tmContributionObjectShape,
  }
}

export default useSchemaShapes
